import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Slider } from "../components/ui/slider";

export const HonorairesPage = () => {
  const [budget, setBudget] = useState(500000);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const calculateRecherche = useMemo(() => {
    if (budget <= 1000000) {
      return budget * 0.025;
    } else {
      return 1000000 * 0.025 + (budget - 1000000) * 0.02;
    }
  }, [budget]);

  const calculateVente = useMemo(() => {
    if (budget <= 1000000) {
      return budget * 0.04;
    } else {
      return 1000000 * 0.04 + (budget - 1000000) * 0.035;
    }
  }, [budget]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div data-testid="honoraires-page" className="pt-32">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold text-sm font-bold uppercase tracking-[0.2em] mb-4">
              Tarification
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
              Honoraires
            </h1>
            <p className="text-text-secondary text-lg">
              Une tarification claire pour chaque service
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tarifs Section */}
      <section data-testid="tarifs-section" className="py-16 md:py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Recherche */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="tarif-recherche"
              className="bg-background border border-line p-8"
            >
              <h2 className="text-2xl font-bold text-text-primary mb-6">
                Recherche d'un bien
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-line">
                  <span className="text-text-secondary">Jusqu'à 1 000 000 €</span>
                  <span className="text-gold font-bold">2,5% TTC</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-text-secondary">Au-delà de 1 000 000 €</span>
                  <span className="text-gold font-bold">2% TTC</span>
                </div>
              </div>
            </motion.div>

            {/* Vente */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="tarif-vente"
              className="bg-background border border-line p-8"
            >
              <h2 className="text-2xl font-bold text-text-primary mb-6">
                Vente d'un bien
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-line">
                  <span className="text-text-secondary">Jusqu'à 1 000 000 €</span>
                  <span className="text-gold font-bold">4% TTC</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-text-secondary">Au-delà de 1 000 000 €</span>
                  <span className="text-gold font-bold">3,5% TTC</span>
                </div>
              </div>
            </motion.div>

            {/* Valorisation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              data-testid="tarif-valorisation"
              className="bg-background border border-line p-8"
            >
              <h2 className="text-2xl font-bold text-text-primary mb-6">
                Valorisation d'un bien
              </h2>
              <div className="space-y-4">
                <div className="py-3">
                  <p className="text-text-secondary">Sur devis personnalisé</p>
                  <p className="text-gold font-bold mt-2">Devis dans les 48h ouvrées sur rendez-vous</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Legal mention */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-text-secondary text-sm text-center max-w-3xl mx-auto mb-8"
          >
            Les honoraires sont calculés sur le prix net vendeur et dus uniquement en cas de succès, 
            lors de la signature de l'acte authentique. Ils représentent les pourcentages et prix 
            maximums applicables à nos prestations.
          </motion.p>

          {/* Grouped services info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto bg-background border border-gold/30 p-8 text-center"
          >
            <p className="text-gold font-semibold mb-2">Prestations groupées</p>
            <p className="text-text-secondary">
              Dans le cadre de prestations groupées, vente + recherche, pour vous remercier de votre confiance, 
              nous alignons nos tarifs pour l'ensemble sur les honoraires les plus basses, à savoir la recherche.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Simulator Section */}
      <section data-testid="simulator-section" className="py-16 md:py-24 bg-background">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary text-center mb-12">
              Simulateur de budget
            </h2>

            <div className="bg-surface border border-gold/30 p-8 md:p-12">
              {/* Budget Input */}
              <div className="mb-12">
                <label className="block text-text-primary font-bold mb-4">
                  Prix du bien
                </label>
                <div className="space-y-6">
                  <Slider
                    data-testid="budget-slider"
                    value={[budget]}
                    onValueChange={(value) => setBudget(value[0])}
                    min={100000}
                    max={5000000}
                    step={10000}
                    className="w-full"
                  />
                  <div className="flex justify-between text-text-secondary text-sm">
                    <span>100 000 €</span>
                    <span className="text-gold text-2xl font-bold">{formatPrice(budget)}</span>
                    <span>5 000 000 €</span>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div
                  data-testid="result-recherche"
                  className="bg-background border border-line p-6 text-center"
                >
                  <p className="text-text-secondary mb-2">Honoraires de recherche</p>
                  <p className="text-gold text-3xl font-bold">{formatPrice(calculateRecherche)}</p>
                  <p className="text-text-secondary text-sm mt-2">TTC</p>
                </div>
                <div
                  data-testid="result-vente"
                  className="bg-background border border-line p-6 text-center"
                >
                  <p className="text-text-secondary mb-2">Honoraires de vente</p>
                  <p className="text-gold text-3xl font-bold">{formatPrice(calculateVente)}</p>
                  <p className="text-text-secondary text-sm mt-2">TTC</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
